import * as THREE from 'three';
import { CONFIG } from '../app/config.js';

export class SceneManager {
  constructor(containerElement) {
    this.container = containerElement;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.ambientLight = null;
    this.dirLight = null;
    this.isDisposed = false;

    this.onWindowResize = this.onWindowResize.bind(this);
  }

  static isWebGLAvailable() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
    } catch {
      return false;
    }
  }

  init() {
    if (!SceneManager.isWebGLAvailable()) {
      return false;
    }

    // 1. Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0b1020);
    this.scene.fog = new THREE.FogExp2(0x0b1020, 0.015);

    // 2. Camera
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    const aspect = width / height;

    this.camera = new THREE.PerspectiveCamera(
      CONFIG.GRAPHICS.FOV,
      aspect,
      CONFIG.GRAPHICS.NEAR,
      CONFIG.GRAPHICS.FAR
    );
    this.camera.position.set(
      CONFIG.GRAPHICS.CAMERA_OFFSET.x,
      CONFIG.GRAPHICS.CAMERA_OFFSET.y,
      CONFIG.GRAPHICS.CAMERA_OFFSET.z
    );
    this.camera.lookAt(
      CONFIG.GRAPHICS.CAMERA_LOOKAT.x,
      CONFIG.GRAPHICS.CAMERA_LOOKAT.y,
      CONFIG.GRAPHICS.CAMERA_LOOKAT.z
    );

    // 3. Renderer
    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
      alpha: false,
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, CONFIG.GRAPHICS.MAX_DPR));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;

    this.container.appendChild(this.renderer.domElement);

    // 4. Lighting
    this.setupLighting();

    // 5. Resize Listener
    window.addEventListener('resize', this.onWindowResize, false);

    return true;
  }

  setupLighting() {
    // Soft ambient / hemisphere light for consistent neon fill
    this.ambientLight = new THREE.HemisphereLight(0x28385e, 0x070b14, 1.2);
    this.scene.add(this.ambientLight);

    // Key directional light highlighting player silhouette & track
    this.dirLight = new THREE.DirectionalLight(0x42e8e0, 1.8);
    this.dirLight.position.set(10, 20, 15);
    this.scene.add(this.dirLight);

    // Subtle warm rim/backlight
    const rimLight = new THREE.DirectionalLight(0xf05bd5, 1.2);
    rimLight.position.set(-10, 12, -20);
    this.scene.add(rimLight);
  }

  onWindowResize() {
    if (!this.renderer || !this.camera) return;
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, CONFIG.GRAPHICS.MAX_DPR));
  }

  render() {
    if (this.renderer && this.scene && this.camera && !this.isDisposed) {
      this.renderer.render(this.scene, this.camera);
    }
  }

  dispose() {
    this.isDisposed = true;
    window.removeEventListener('resize', this.onWindowResize);

    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
  }
}
